import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSeo } from "@/components/feature/Seo";

const origin = "https://focuspeople.example";

const principles = [
  {
    icon: "ri-scales-3-line",
    title: "只标注，不预判",
    text: "将人物区分为关键知情人、涉事主角、关联当事人等角色，仅为信息梳理所需，不做超出证据的结论性判断。",
  },
  {
    icon: "ri-file-shield-2-line",
    title: "证据优先，多源互证",
    text: "每条写入档案的信息都要求可验证旁证或交叉印证，未经核实的内容一律注明「未经独立核实」。",
  },
  {
    icon: "ri-user-heart-line",
    title: "保护未定论的对象",
    text: "对尚未有结论的信源与当事人采用化名呈现，避免核实之前给具体个人造成不必要的影响。",
  },
  {
    icon: "ri-history-line",
    title: "随进展持续更新",
    text: "材料补充、回应口径变化与关系调整都标注日期与状态，让读者能够回溯事件完整演变。",
  },
];

const voices = [
  {
    name: "追焦爱好者 @Ling",
    text: "以往刷到爆料只能看热闹，这里把人物角色和关联讲得很清楚，谁是谁一下就顺了。",
  },
  {
    name: "内容研究者 阿May",
    text: "最打动我的是克制，材料没核实前不会乱下结论，这种处理在同类内容里很少见。",
  },
  {
    name: "行业读者 老周",
    text: "时间线和状态标注很实用，隔几天回来就能看到进展，不用自己翻聊天记录拼。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们｜焦点人物志 · 最新爆料人物追踪",
    description:
      "焦点人物志专注最新爆料中的人物追踪，介绍网站定位、发展理念与内容原则：只标注不预判、证据优先多源互证，并展示读者口碑与联系方式。",
    keywords: "关于我们,人物追踪,最新爆料,内容原则,焦点人物志",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们",
        url: `${origin}/guanyu`,
        description:
          "焦点人物志的网站定位、发展理念、内容原则、读者口碑与联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "焦点人物志",
        url: origin,
        email: "contact@focuspeople.example",
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 pt-8 md:px-6">
          <nav
            aria-label="面包屑"
            className="flex items-center gap-1.5 text-xs text-secondary-700"
          >
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-950">关于我们</span>
          </nav>

          <header className="mt-6 rounded-lg bg-foreground-950 px-6 py-10 text-center md:px-10 md:py-14">
            <p className="inline-flex items-center gap-2 rounded-full bg-background-50/10 px-3 py-1 text-xs font-semibold tracking-widest text-background-50/80">
              ABOUT US
            </p>
            <h1 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-black leading-tight text-background-50 md:text-4xl">
              在纷杂的爆料场中，
              <br />
              我们选择回到人本身
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-background-50/80 md:text-base">
              「焦点人物志」是一档专注最新爆料中人物追踪的简体中文内容栏目。当每一个新主角、新面孔、新涉事者与新当事人浮出水面，我们便以人物档案的方式，帮读者厘清谁是谁、谁和谁有关、事情推进到哪一步。
            </p>
          </header>
        </div>

        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6">
          <section>
            <h2 className="font-display text-2xl font-bold text-foreground-950">
              我们的定位与理念
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-display text-lg font-bold text-foreground-950">
                  网站定位
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  我们不做情绪化的「谁对谁错」判官，而是做信息纷杂时的「人物地图」：把同一桩热点中的关键知情人、被点名者、关联当事人与回应代表整理成清晰的档案与关系脉络，供读者各取所需地快速掌握全局。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-display text-lg font-bold text-foreground-950">
                  发展理念
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  我们相信，追热点不是追流量，而是追事实。相比抢先，我们更在意每一次更新都站得住脚；相比热闹，我们更在意把复杂的人物关系讲清楚。慢一点、稳一点，是内容值得被信任的底气。
                </p>
              </div>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold text-foreground-950">
              我们坚持的内容原则
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p) => (
                <article
                  key={p.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500 text-lg text-background-50">
                    <i className={p.icon}></i>
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold text-foreground-950">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                    {p.text}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-lg bg-background-100 px-6 py-10 md:px-10">
            <h2 className="text-center font-display text-2xl font-bold text-foreground-950">
              读者怎么说
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((v) => (
                <figure
                  key={v.name}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <i className="ri-double-quotes-l text-2xl text-primary-100"></i>
                  <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">
                    {v.text}
                  </blockquote>
                  <figcaption className="mt-4 text-sm font-semibold text-foreground-950">
                    {v.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>

          <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-lg bg-primary-500 px-6 py-8 text-background-50">
              <h2 className="font-display text-xl font-bold">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-background-50/90">
                欢迎通过以下方式与我们交流线索、反馈建议或合作。收到来信后，我们会尽快回复。
              </p>
              <div className="mt-5 space-y-3 text-sm">
                <p className="flex items-center gap-2.5">
                  <i className="ri-mail-line"></i>
                  contact@focuspeople.example
                </p>
                <p className="flex items-center gap-2.5">
                  <i className="ri-refresh-line"></i>
                  内容每有新进展即同步更新
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-display text-lg font-bold text-foreground-950">
                想从首页开始看？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                从最新动态到人物档案，再到追踪方法与常见问题，所有板块都汇总在首页，你可以按需顺序阅读。
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/renwu"
                  className="whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-center text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  进入人物档案库
                </Link>
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-center text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}